import type {
  ListingsAPIResponseNumericAttribute,
  ListingsAPIResponsePagination,
  ListingsAPIResponseProduct,
  ListingsAPIResponseTextAttribute,
} from '@noths/polaris-client-listings-api';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { deliveryZoneCodes } from '@noths/polaris-client-localisation';
import type { CurrencyCode, Navigation, Price } from '@noths/polaris-dev-ts-types';

import type { RequestProductsActionProductsPayload } from 'src/redux/products/types';
import type { Product, ProductDelivery } from 'src/redux/products/types/Product';
import { PRODUCT_IMAGE_LIMIT } from 'src/redux/products/types/Product';

interface AdaptResponseProductsToStoreArgs {
  currencyCode: CurrencyCode;
  deliveryZone: DeliveryZoneCode;
  metadata: { attributionToken: string; filter?: string };
  pagination: ListingsAPIResponsePagination;
  products: ListingsAPIResponseProduct[];
  term: string;
}

export const adaptResponseProductsToStore = ({
  currencyCode,
  deliveryZone,
  metadata,
  pagination,
  products,
  term,
}: AdaptResponseProductsToStoreArgs): RequestProductsActionProductsPayload => {
  const category = null;
  const navigation: Navigation[] = [];
  const providerMetadata = metadata.filter ? { filter: metadata.filter } : null;
  const partner = null;
  const providerAttributionToken = metadata.attributionToken || null;

  const {
    currentPage,
    pageSize: perPage,
    resultsReturnedThisPage: productsReturnedCount,
    totalResults: totalProducts,
  } = pagination;

  const totalPages = Math.ceil(totalProducts / perPage);

  const adaptedProducts = products.map((product) => {
    const {
      attributes,
      available,
      code,
      images,
      isExpandedSearch,
      isNew,
      links,
      onSale,
      partner: { name: partnerName, shortcode: partnerShortcode },
      preSalePrices,
      price,
      purchasable,
      ratings,
      salePercentage,
      title,
    } = product;

    const isMainlandUKDeliveryZone = deliveryZone === deliveryZoneCodes[1];

    const deliveryFreeDays = isMainlandUKDeliveryZone
      ? (attributes?.delivery_days_FREE as ListingsAPIResponseNumericAttribute)?.numbers?.[0] ??
        null
      : null;
    const standardDeliveryCost = isMainlandUKDeliveryZone
      ? (attributes?.delivery_cost_STANDARD as ListingsAPIResponseNumericAttribute)?.numbers?.[0] ??
        null
      : null;
    const standardDeliveryDays = isMainlandUKDeliveryZone
      ? (attributes?.delivery_days_STANDARD as ListingsAPIResponseNumericAttribute)?.numbers?.[0] ??
        null
      : null;
    // A free option wins on price, so it is preferred outright over the standard
    // one, and its cost is known to be zero without reading an attribute.
    // Otherwise the standard option is quoted, and only when both of its halves
    // are present: one half alone cannot describe an option.
    let delivery: ProductDelivery | null = null;
    if (deliveryFreeDays !== null) {
      delivery = { cost: 0, days: deliveryFreeDays };
    } else if (standardDeliveryCost !== null && standardDeliveryDays !== null) {
      delivery = { cost: standardDeliveryCost, days: standardDeliveryDays };
    }

    // `attributes` is a passthrough of every Vertex custom attribute, including
    // commercially sensitive ones (e.g. `actual_comms_rate`), so it is never
    // spread. Only named keys are read, here and above for delivery.
    //
    // `isHighlyRated`/`isTopBrand` from `@noths/polaris-client-listings-api` look like
    // the obvious reuse, but they are typed for the recommendations endpoint, whose
    // `ITextAttribute.text` and `INumberAttribute.numbers` are required where this
    // endpoint's are optional. Neither union member is assignable, so calling them
    // here is a compile error, not a style choice. The cast below is narrower than
    // the one that would be needed to paper over that schema difference.
    const highlyRated =
      (attributes?.highly_rated as ListingsAPIResponseTextAttribute)?.text?.[0] === 'true';
    // Named `star_brand` in the catalogue; there is no `top_brand` attribute.
    const starBrand =
      (attributes?.star_brand as ListingsAPIResponseTextAttribute)?.text?.[0] === 'true';
    // `Number.isFinite` covers every way the API can fail to give us a number:
    // absent, null, or a non-numeric value. Zero is excluded deliberately: the API
    // returns an average of 0 alongside a count of 0 for a product nobody has
    // reviewed, and the card would render that as a "0" rating rather than as no
    // rating at all.
    const averageRating = ratings?.averageRating;
    const hasRating =
      typeof averageRating === 'number' && Number.isFinite(averageRating) && averageRating > 0;

    // Allowlist the fields that are actually read during initial render, tracking
    // or SEO. Anything else from the API response (e.g. `prices`,
    // `inventoryStatus`, `partner.links`) is intentionally dropped so it is not
    // serialised into the initial page source.
    //
    // `images` is the one field kept as an array rather than reduced to a single
    // value. The order is carried deliberately, so the card can show more than the
    // primary image, but only up to `PRODUCT_IMAGE_LIMIT`: the API puts no bound on
    // the array and the card renders no more than that, so the rest would be
    // serialised into every page and then discarded at render. The slice comes after
    // the empty hrefs are dropped, so a blank entry near the front costs the product
    // a placeholder rather than a real image. The per-image `title` and `gravity` are
    // still dropped: the title duplicates `title` below, and nothing reads the gravity.
    const adaptedProduct: Product = {
      ...(hasRating ? { averageRating } : {}),
      available,
      code,
      delivery,
      detailPageHref: links?.[0]?.rel || '',
      // Omitted rather than defaulted, as with `averageRating` above and `starBrand`
      // below, so a product without them adds nothing to the serialised page.
      ...(highlyRated ? { highlyRated } : {}),
      images:
        images
          ?.map(({ href }) => href)
          .filter(Boolean)
          .slice(0, PRODUCT_IMAGE_LIMIT) ?? [],
      isExpandedSearch,
      isNew,
      onSale,
      // The listings API can return a partner with a missing/undefined `name` or
      // `shortcode` (e.g. inactive partners). Coerce to '' so no `undefined`
      // reaches the Redux store — Next.js cannot serialize `undefined` values in
      // `getServerSideProps`.
      partner: { name: partnerName ?? '', shortcode: partnerShortcode ?? '' },
      // The API types `currency` as a plain string; the store narrows it to a
      // CurrencyCode union. This narrowing was previously implicit via the
      // `as Product` cast applied to the whole spread object.
      preSalePrices: preSalePrices as Price[],
      price: price as Price,
      purchasable,
      salePercentage,
      ...(starBrand ? { starBrand } : {}),
      title,
    };

    return adaptedProduct;
  });

  const result = {
    category,
    currencyCode,
    products: adaptedProducts,
    perPage,
    lastPageLoaded: currentPage,
    totalPages,
    totalProducts,
    productsReturnedCount,
    term,
    navigation,
    partner,
    providerAttributionToken,
    providerMetadata,
  };
  return result;
};
